/**
 * **The two date fields at the top of a tab**, and the sentence that refuses what they were set to.
 *
 * Trendy and Ankiety draw the same bar: one eyebrow labelling the pair, two native date inputs
 * wearing the four bounds the model computed, an arrow between them, a note at the end, and a
 * refusal under it. The two copies were already eleven props apart from being one — and the parts
 * that are easy to lose in a copy are the ones that matter: `aria-invalid` and `aria-describedby`
 * appear on BOTH fields only while the range is refused, and `min`/`max` come from the model so the
 * picker refuses most illegal ranges before they are typed.
 *
 * ── What stays the caller's ──────────────────────────────────────────────────────────────────
 * The NOTE, because the two tabs count different things — a span in days on Trendy, occurrences in
 * range on Ankiety — and `children`, because Ankiety puts its form chips on the same bar ahead of
 * the eyebrow. Neither is a variation this file should know about.
 *
 * ── `entrance` is the Motion props, spread onto the bar itself ───────────────────────────────
 * Not a `reducedMotion` flag: the two tabs differ in whether the bar has an entrance at all
 * (Ankiety's is index 0 of a three-step cascade; Trendy's has none), and the alternative was a
 * wrapper element around the bar, which would have put a block box inside a flex row for no reason.
 * A caller with no entrance passes nothing and the `motion.div` renders the same `<div>` it always
 * did.
 */

import { motion, type HTMLMotionProps } from 'motion/react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import eyebrows from './cardEyebrow.module.css';
import styles from './DetailTab.module.css';

import type { TrendsRange, TrendsRangeView } from '../model/trendsRange';

export interface DateRangeBarProps {
  readonly range: TrendsRange;
  /** The four field bounds and the refusal, from the tab's own `model/` decision. */
  readonly bounds: TrendsRangeView;
  readonly onChange: (range: TrendsRange) => void;
  /** What this tab counts, at the far end of the bar. */
  readonly note: string;
  /** Anything that stands BEFORE the eyebrow — Ankiety's form chips. */
  readonly children?: ReactNode;
  readonly entrance?: HTMLMotionProps<'div'>;
}

export function DateRangeBar({
  range,
  bounds,
  onChange,
  note,
  children,
  entrance,
}: DateRangeBarProps) {
  const { t } = useTranslation();
  const refusalId = useId();

  return (
    <>
      <motion.div className={styles.rangeBar} {...entrance}>
        {children}

        {/* One label for the pair, which is what the canon draws. Each field then carries its own
          * accessible name, and there is no visible per-field label for it to have to contain. */}
        <em className={eyebrows.eyebrow}>{t('web:clientDetail.trends.rangeEyebrow')}</em>
        <input
          type="date"
          className={styles.rangeInput}
          aria-label={t('web:clientDetail.trends.from')}
          value={range.from}
          min={bounds.fromMin}
          max={bounds.fromMax}
          aria-invalid={bounds.valid ? undefined : true}
          aria-describedby={bounds.valid ? undefined : refusalId}
          onChange={(event) => {
            onChange({ ...range, from: event.target.value });
          }}
        />
        <span className={styles.rangeArrow} aria-hidden="true">
          →
        </span>
        <input
          type="date"
          className={styles.rangeInput}
          aria-label={t('web:clientDetail.trends.to')}
          value={range.to}
          min={bounds.toMin}
          max={bounds.toMax}
          aria-invalid={bounds.valid ? undefined : true}
          aria-describedby={bounds.valid ? undefined : refusalId}
          onChange={(event) => {
            onChange({ ...range, to: event.target.value });
          }}
        />
        <em className={styles.rangeNote}>{note}</em>
      </motion.div>

      {/* The refusal carries the REASON, not the rule. "At least 7 days" alone reads as a limit
        * somebody chose; saying that volume is a weekly rate is what makes the bound obviously
        * right and stops the trainer working around it. `role="alert"` because it appears in
        * response to something they just typed. */}
      {bounds.refusal === null ? null : (
        <p className={styles.rangeRefusal} id={refusalId} role="alert">
          {bounds.refusal}
        </p>
      )}
    </>
  );
}
